import React, { useCallback, useState } from 'react';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { WarehouseLayout } from '@components/layout/WarehouseLayout';
import { ReceiveStockModal } from '@components/organisms/ReceiveStockModal';
import { LowStockAlertCard } from '@components/organisms/LowStockAlertCard';
import {
  useLowStockAlerts,
  formatAlertNumber,
} from '@hooks/api/warehouse/useLowStockAlerts';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LeadTimeAlertsSection from './components/LeadTimeAlertsSection';

const LowStockAlertsContent: React.FC = () => {
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState<
    string | undefined
  >();

  // Shared low-stock alert data (also used by the dashboard slide-over). Shows
  // alerts across ALL warehouses, grouped by warehouse, so the count matches the
  // global sidebar badge and each alert's origin is clear.
  const { alertItems, warehouseGroups, isLoading, refetch } =
    useLowStockAlerts();

  const handleOrder = useCallback((catalogId: string) => {
    setSelectedCatalogId(catalogId);
    setIsReceiveModalOpen(true);
  }, []);

  const handleCloseReceiveModal = useCallback(() => {
    setIsReceiveModalOpen(false);
    setSelectedCatalogId(undefined);
  }, []);

  const handleReceiveSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loading size="xl" center />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <WarningAmberIcon className="text-amber-500" sx={{ fontSize: 24 }} />
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Warehouse Alerts
            </h1>
            <p className="text-xs text-gray-400">
              Low stock and material order deadlines
            </p>
          </div>
        </div>

        <Button variant="tertiary" onClick={refetch}>
          <RefreshIcon sx={{ fontSize: 16 }} />
        </Button>
      </div>

      {/* All-warehouses explanatory banner */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700">
        <WarehouseIcon sx={{ fontSize: 18 }} className="mt-0.5 flex-shrink-0" />
        <span>
          Showing low-stock alerts across <strong>all warehouses</strong>,
          grouped by warehouse.
        </span>
      </div>

      {/* No Alerts State */}
      {alertItems.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-10 text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Inventory2Icon className="text-green-600" sx={{ fontSize: 28 }} />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">
            All Stock Levels OK
          </h3>
          <p className="text-sm text-gray-400">
            No items are currently below their reorder point.
          </p>
        </div>
      )}

      {/* Low stock grouped by warehouse (critical items first within each group) */}
      {warehouseGroups.map((group) => (
        <div key={group.warehouseId} className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
            <WarehouseIcon sx={{ fontSize: 16 }} className="text-gray-400" />
            {group.name}
            <span className="text-xs font-medium text-gray-400 normal-case">
              ({group.items.length})
            </span>
          </h2>
          {group.items.map((item) => (
            <LowStockAlertCard
              key={item.stockLevel.stockLevelId}
              item={item}
              onOrder={handleOrder}
              formatNumber={formatAlertNumber}
            />
          ))}
        </div>
      ))}

      {/* Receive Stock Modal */}
      <ReceiveStockModal
        isOpen={isReceiveModalOpen}
        onClose={handleCloseReceiveModal}
        initialCatalogId={selectedCatalogId}
        onSuccess={handleReceiveSuccess}
      />
    </div>
  );
};

const LowStockAlertsPage: React.FC = () => {
  return (
    <WarehouseLayout>
      <div className="space-y-10">
        <LowStockAlertsContent />
        <LeadTimeAlertsSection />
      </div>
    </WarehouseLayout>
  );
};

export default LowStockAlertsPage;
